<script setup lang="ts">
  import { cva } from 'class-variance-authority'
  import type { ButtonLinkStoryblok } from '@/storyblok/component-types-sb'
  import type { ILinkButton } from '@/components/ui/link/types'
  import { getCompactLinkData } from '@/utils/storyblok/getCompactLinkData'
  import { pushValidatedDataLayer } from '@/utils/dataLayer'
  import type { IButtonLinkDataLayer } from '@/types/DataLayer'

  const props = withDefaults(
    defineProps<{
      blok: ButtonLinkStoryblok
      fullWidthOnTablet?: boolean
      size?: ILinkButton['size']
    }>(),
    {
      fullWidthOnTablet: false,
      size: 'md'
    }
  )

  const localeStore = useLocaleStore()

  const buttonLink = getCompactLinkData(props.blok.link)
  const title = computed(() => props.blok.label || buttonLink.text)
  const target = computed(() => (props.blok.open_in_new_tab ? '_blank' : '_self'))

  const buttonClassCva = cva('inline-flex w-full items-center justify-center font-medium', {
    variants: {
      fullWidthOnTablet: {
        false: 'sm:mx-auto sm:w-80 md:mx-0 md:w-fit',
        true: 'sm:w-full md:mx-0 md:w-fit'
      }
    }
  })

  const buttonClass = computed(() =>
    buttonClassCva({
      fullWidthOnTablet: props.fullWidthOnTablet
    })
  )

  function onClick() {
    if (!props.blok.enableDataLayerEvent) return

    const data: IButtonLinkDataLayer = {
      event: props.blok.dataLayerEvent ?? '',
      event_category: props.blok.dataLayerCategory ?? '',
      event_action: 'click',
      page_type: props.blok.dataLayerPageType ?? '',
      site_language: localeStore.selectedLanguage
    }

    pushValidatedDataLayer(data)
  }
</script>

<template>
  <UiLinkButton
    :url="buttonLink.href"
    :title="title"
    :variant="blok.style"
    :size="size"
    :class="buttonClass"
    :target="target"
    @click="onClick"
  />
</template>
